/** WebSocket hook for real-time trading signal updates. */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TradingAnalysis } from '../types/trading';

const WS_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8001')
  .replace('http://', 'ws://')
  .replace('https://', 'wss://');

interface TradingWSMessage {
  type: string;
  timestamp: string;
  data?: Record<string, unknown>;
  message?: string;
}

export function useTradingWebSocket() {
  const [connected, setConnected] = useState(false);
  const [lastSignal, setLastSignal] = useState<TradingAnalysis | null>(null);
  const [alerts, setAlerts] = useState<TradingWSMessage[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<number | null>(null);
  const queryClient = useQueryClient();

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_URL}/api/v1/trading/ws`);

    ws.onopen = () => {
      setConnected(true);
      console.log('Trading WS connected');
    };

    ws.onmessage = (event) => {
      try {
        const msg: TradingWSMessage = JSON.parse(event.data);

        if (msg.type === 'new_signal' && msg.data) {
          setLastSignal(msg.data as unknown as TradingAnalysis);
          // Invalidate analyses query so dashboard refreshes
          queryClient.invalidateQueries({ queryKey: ['trading-analyses'] });
        } else if (msg.type === 'job_update') {
          // Refresh job queue + analyses when a job completes
          queryClient.invalidateQueries({ queryKey: ['analysis-jobs'] });
          if (msg.data?.status === 'completed') {
            queryClient.invalidateQueries({ queryKey: ['trading-analyses'] });
            queryClient.invalidateQueries({ queryKey: ['trading-confirmations'] });
          }
        } else if (msg.type === 'daily_review' || msg.type === 'weekly_review' || msg.type === 'monthly_review') {
          queryClient.invalidateQueries({ queryKey: ['trading-insights'] });
          setAlerts((prev) => [msg, ...prev].slice(0, 20));
        } else if (msg.type === 'performance_alert') {
          setAlerts((prev) => [msg, ...prev].slice(0, 20));
        }
      } catch {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      setConnected(false);
      // Auto-reconnect after 5s
      reconnectTimer.current = window.setTimeout(connect, 5000);
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  }, [queryClient]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  // Ping every 30s to keep connection alive
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return { connected, lastSignal, alerts };
}
